import {
  ManagerPerformanceInsightDto,
  UserFilter,
} from "@coaching-culture/types";
import {
  Button,
  DateRangeButton,
  Flex,
  formatDate,
  GaugeChart,
  Grid,
  Loader,
  Panel,
  SortableTable,
  SortableTableColumn,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { useEffect, useMemo, useState } from "react";

import { useGoalTerminology } from "auth/OrgProvider";
import axios from "axios";
import { FilterModal } from "components/FilterPanel";
import { addDays, endOfDay, fromUnixTime, startOfDay } from "date-fns";
import qsStringify from "qs-stringify";
import { saveExportAsCsv } from "utils";
import { isOverdue } from "utils/content";

export const PerformManagerInsights = () => {
  const goalTerms = useGoalTerminology();
  const [data, setData] = useState<ManagerPerformanceInsightDto[] | null>(null);
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [range, setRange] = useState<[Date, Date]>([
    startOfDay(addDays(new Date(), -30)),
    endOfDay(new Date()),
  ]);
  const rangeLabel = `${formatDate(range[0])} - ${formatDate(range[1])}`;

  const colour = (ratio: number, invertColours: boolean): string => {
    if (invertColours) {
      return ratio < 0.01
        ? "positive"
        : ratio >= 0.01 && ratio <= 0.5
          ? "warning"
          : "danger";
    }
    return ratio < 0.5
      ? "danger"
      : ratio >= 0.5 && ratio <= 0.99
        ? "warning"
        : "positive";
  };

  const filterString = qsStringify({ filter } as any);
  useEffect(() => {
    axios
      .get(`/api/v2/insights/perform/managers?${filterString}`)
      .then(({ data }) => setData(data));
  }, [filterString]);

  const cols = useMemo(
    (): SortableTableColumn<ManagerPerformanceInsightDto>[] => [
      {
        name: "name",
        label: "Name",
        format: (x) => (
          <UserFlag user={x} to={`/success/people/users/${x.id}`} />
        ),
        sortFormat: (x) => x.name.toLowerCase(),
        exportFormat: (x) => x.name,
      },
      {
        name: "managers",
        label: "Manager",
        format: (x) => (
          <Flex flexDirection={"column"}>
            {x.managers.map((y) => (
              <UserFlag user={y} to={`/success/people/users/${y.id}`} />
            ))}
          </Flex>
        ),
        sortFormat: (x) => x.managers[0]?.name ?? "",
        exportFormat: (x) => x.managers.map((x) => x.email).join(" "),
      },
      {
        name: "activeGoals",
        label: `Direct Reports with active ${goalTerms.goal.asPluralTitle()}`,
        headingProps: { whiteSpace: "wrap", width: "20%" },
        format: (x) => {
          const active = x.directReports.filter((user) =>
            user.goals.some((goal) => goal.completedOn === null),
          ).length;
          return (
            <Text color={colour(active / x.directReports.length, false)}>
              <strong>
                {active} / {x.directReports.length}
              </strong>
            </Text>
          );
        },
        sortFormat: (x) =>
          x.directReports.filter((user) =>
            user.goals.some((goal) => goal.completedOn === null),
          ).length,
        exportFormat: (x) =>
          x.directReports
            .filter((user) =>
              user.goals.some((goal) => goal.completedOn === null),
            )
            .length.toString() +
          " / " +
          x.directReports.length,
      },
      {
        name: "overdueGoals",
        label: `Direct Reports with overdue ${goalTerms.goal.asPluralTitle()}`,
        headingProps: { whiteSpace: "wrap", width: "20%" },
        format: (x) => {
          const overdue = x.directReports.filter((user) =>
            user.goals.some(isOverdue),
          );
          return (
            <Text color={colour(overdue.length / x.directReports.length, true)}>
              <strong>
                {overdue.length} / {x.directReports.length}
              </strong>
            </Text>
          );
        },
        sortFormat: (x) =>
          x.directReports.filter((user) =>
            user.goals.some((goal) => goal.completedOn === null && isOverdue),
          ).length,
        exportFormat: (x) =>
          x.directReports
            .filter((user) =>
              user.goals.some((goal) => goal.completedOn === null && isOverdue),
            )
            .length.toString() +
          " / " +
          x.directReports.length,
      },

      {
        name: "completedConversation",
        label: `Completed Conversation (${rangeLabel})`,
        headingProps: { whiteSpace: "wrap", width: "20%" },
        format: (x) => {
          const completedConversations = x.directReports.filter((user) =>
            user.conversations.some(
              (convo) =>
                convo.completedOn !== null &&
                fromUnixTime(convo.completedOn) >= range[0] &&
                fromUnixTime(convo.completedOn) <= range[1],
            ),
          );

          return (
            <Text
              color={colour(
                completedConversations.length / x.directReports.length,
                false,
              )}
            >
              <strong>
                {completedConversations.length} / {x.directReports.length}
              </strong>
            </Text>
          );
        },
        exportFormat: (x) => {
          const completedConversations = x.directReports.filter((user) =>
            user.conversations.some(
              (convo) =>
                convo.completedOn !== null &&
                fromUnixTime(convo.completedOn) >= range[0] &&
                fromUnixTime(convo.completedOn) <= range[1],
            ),
          );

          return completedConversations.length + " / " + x.directReports.length;
        },
        sortFormat: (x) => {
          const completedConversations = x.directReports.filter((user) =>
            user.conversations.some(
              (convo) =>
                convo.completedOn !== null &&
                fromUnixTime(convo.completedOn) >= range[0] &&
                fromUnixTime(convo.completedOn) <= range[1],
            ),
          );

          return completedConversations.length;
        },
      },
      {
        name: "sentFeedback",
        label: `Direct reports who sent feedback (${rangeLabel})`,
        headingProps: { whiteSpace: "wrap", width: "20%" },
        format: (x) => {
          const feedbackSent = x.directReports.filter((user) =>
            user.sentFeedback.some(
              (feedback) =>
                fromUnixTime(feedback.sentOn) >= range[0] &&
                fromUnixTime(feedback.sentOn) <= range[1],
            ),
          );

          return (
            <Text
              color={colour(
                feedbackSent.length / x.directReports.length,
                false,
              )}
            >
              <strong>
                {feedbackSent.length} / {x.directReports.length}
              </strong>
            </Text>
          );
        },
        exportFormat: (x) => {
          const feedbackSent = x.directReports.filter((user) =>
            user.sentFeedback.some(
              (feedback) =>
                fromUnixTime(feedback.sentOn) >= range[0] &&
                fromUnixTime(feedback.sentOn) <= range[1],
            ),
          );

          return feedbackSent.length + " / " + x.directReports.length;
        },
        sortFormat: (x) => {
          const feedbackSent = x.directReports.filter((user) =>
            user.sentFeedback.some(
              (feedback) =>
                fromUnixTime(feedback.sentOn) >= range[0] &&
                fromUnixTime(feedback.sentOn) <= range[1],
            ),
          );

          return feedbackSent.length;
        },
      },
      {
        name: "receivedFeedback",
        label: `Direct reports who received feedback (${rangeLabel})`,
        headingProps: { whiteSpace: "wrap", width: "20%" },
        format: (x) => {
          const feedbackReceived = x.directReports.filter((user) =>
            user.receivedFeedback.some(
              (feedback) =>
                fromUnixTime(feedback.sentOn) >= range[0] &&
                fromUnixTime(feedback.sentOn) <= range[1],
            ),
          );

          return (
            <Text
              color={colour(
                feedbackReceived.length / x.directReports.length,
                false,
              )}
            >
              <strong>
                {feedbackReceived.length} / {x.directReports.length}
              </strong>
            </Text>
          );
        },
        exportFormat: (x) => {
          const feedbackReceived = x.directReports.filter((user) =>
            user.receivedFeedback.some(
              (feedback) =>
                fromUnixTime(feedback.sentOn) >= range[0] &&
                fromUnixTime(feedback.sentOn) <= range[1],
            ),
          );

          return feedbackReceived.length + " / " + x.directReports.length;
        },
        sortFormat: (x) => {
          const feedbackReceived = x.directReports.filter((user) =>
            user.receivedFeedback.some(
              (feedback) =>
                fromUnixTime(feedback.sentOn) >= range[0] &&
                fromUnixTime(feedback.sentOn) <= range[1],
            ),
          );

          return feedbackReceived.length;
        },
      },
    ],
    [range, rangeLabel, goalTerms],
  );

  if (data === null) {
    return <Loader />;
  }

  const upcomingConvos = data.filter((manager) =>
    manager.conversations.some(
      (convo) =>
        fromUnixTime(convo.scheduledDate) > new Date() &&
        convo.cancelledOn === null,
    ),
  ).length;

  const recentConvosAllDirectReports = data.filter((x) =>
    x.directReports.every(
      (user) =>
        user.conversations.filter((convo) => {
          const d = fromUnixTime(convo.scheduledDate);
          return d >= range[0] && d <= range[1] && convo.cancelledOn === null;
        }).length > 0,
    ),
  );

  const activeGoals = data.filter((manager) =>
    manager.directReports.every((user) =>
      user.goals.some((goal) => goal.completedOn === null),
    ),
  );

  const onCancel = (result) => {
    if (result) {
      setFilter(result);
    }

    setShowFilters(false);
  };

  return (
    <>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        filteredUsers={data.map((x) => x.id)}
        onClose={(result) => {
          onCancel(result);
        }}
      />
      <Flex mb={3} justifyContent={"flex-end"}>
        <Button color="primary" onClick={() => setShowFilters(true)}>
          Filters ({filter.groups.length + filter.userFields.length})
        </Button>

        <DateRangeButton
          ml={2}
          value={range}
          onChange={(val) => setRange([startOfDay(val[0]), endOfDay(val[1])])}
          noNull
          presets={[14, 30, 90, 180, 365]}
        />
      </Flex>

      <Grid
        width={"100%"}
        gridTemplateColumns={["1fr", "1fr", "1fr 1fr 1fr"]}
        gridGap={3}
        mb={3}
      >
        <Flex flexDirection={"column"}>
          <Panel p={3}>
            <Text
              fontSize="small"
              fontWeight={500}
              color="textMuted"
              textAlign="center"
              style={{ minHeight: 40 }}
            >
              Managers with active {goalTerms.goal.asPluralBody()} for all
              direct reports
            </Text>
            <GaugeChart
              value={activeGoals.length}
              total={data.length}
              invertColours={false}
            />
          </Panel>
        </Flex>
        <Flex flexDirection={"column"}>
          <Panel p={3}>
            <Text
              fontSize="small"
              fontWeight={500}
              color="textMuted"
              textAlign="center"
              style={{ minHeight: 40 }}
            >
              Managers with conversations with all direct reports between{" "}
              {formatDate(range[0])} and {formatDate(range[1])}
            </Text>
            <GaugeChart
              value={recentConvosAllDirectReports.length}
              total={data.length}
              invertColours={false}
            />
          </Panel>
        </Flex>
        <Flex flexDirection={"column"}>
          <Panel p={3}>
            <Text
              fontSize="small"
              fontWeight={500}
              color="textMuted"
              textAlign="center"
              style={{ minHeight: 40 }}
            >
              Managers with upcoming conversations
            </Text>
            <GaugeChart
              value={upcomingConvos}
              total={data.length}
              invertColours={false}
            />
          </Panel>
        </Flex>
      </Grid>
      <Panel style={{ overflow: "hidden" }}>
        <SortableTable<ManagerPerformanceInsightDto>
          columns={cols}
          data={data}
          defaultSort={1}
          rowKey="id"
          paged
          onExport={saveExportAsCsv("CCUserExport.csv")}
        />
      </Panel>
    </>
  );
};
